// frontend/src/components/Auth/LoginForm.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Added Link
import { loginUser } from '../../services/api';
import useAuth from '../../hooks/useAuth';
import LoadingSpinner from '../Common/LoadingSpinner'; // Assuming you might want a spinner

const LoginForm = () => {
  const [usernameInput, setUsernameInput] = useState(''); // Can be email or username
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      // Assuming your backend's login endpoint accepts 'username' which can be an email or username
      const loginData = await loginUser({ username: usernameInput, password });
      if (loginData && loginData.token) {
        login(loginData.token, loginData.user); // loginData.user should contain user details
        navigate('/article-feed');
      } else {
        // This case should ideally be handled by loginUser throwing an error
        throw new Error("Login response did not include a token or user data.");
      }
    } catch (err) {
      let errorMessages = [];
      if (err && typeof err === 'object' && !err.message) { // Backend validation errors
        Object.keys(err).forEach(key => {
          if (Array.isArray(err[key])) {
            errorMessages.push(`${key.replace("username", "Username/Email").replace("non_field_errors", "Login error")}: ${err[key].join(' ')}`);
          }
        });
        if (err.detail && errorMessages.length === 0) { // General detail error
          errorMessages.push(err.detail);
        }
        setError(errorMessages.length > 0 ? errorMessages.join('; ') : 'Login failed. Please check your credentials.');
      } else { // Network error or other JS error
        setError(err.message || 'Login failed. Please check your credentials.');
      }
      console.error("Login submit error details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 sm:p-10 shadow-xl rounded-lg border border-gray-200">
      {error && <p className="text-red-600 text-sm text-center bg-red-100 p-3 rounded-md">{error}</p>}
      
      <div>
        <label htmlFor="login-username" className="block text-sm font-medium leading-6 text-gray-900">
          Email address {/* Changed from Username or Email for clarity, as backend uses email */}
        </label>
        <div className="mt-2">
          <input
            id="login-username"
            name="username" // Keep as 'username' if your API endpoint expects that field name for email
            type="email" // Changed to type="email" for better browser validation/UX
            autoComplete="email"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            required
            className="block w-full rounded-md border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label htmlFor="login-password" className="block text-sm font-medium leading-6 text-gray-900">
            Password
          </label>
          <div className="text-sm">
            <Link to="/password-reset" className="font-semibold text-blue-600 hover:text-blue-500">
              Forgot password?
            </Link>
          </div>
        </div>
        <div className="mt-2">
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="block w-full rounded-md border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full flex justify-center py-2.5 px-5 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white transition-colors duration-150 ease-in-out ${
            isLoading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
          }`}
        >
          {isLoading ? (
            <span className="flex items-center">
              <LoadingSpinner /> <span className="ml-2">Signing in...</span>
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </div>
    </form>
  );
};

export default LoginForm;

